/**
 * Photos and video — `docs/activity.md` §11.3, Board 15 B.
 *
 * *‹ Stop · 6 photos · 1 video · ⋯*; where they are saving to; the amber
 * band when there is no signal (*2 photos are saved on this phone and go up
 * when you're back online.*); a grid of 110 px tiles, three across, each
 * saying where it is — a green tick when it is up, a ring while it goes, a
 * *waiting* pill until it can — a video with ▷ and its length; the selected
 * one ringed, and its caption under the grid with Dictate in the field;
 * **Also show on the property's photos** (on); **Take more** · **Done**.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn.js'
import { Dictation } from '../../components/dictation.js'
import { Glyph } from '../glyphs.js'
import { StopSheet } from '../stop/sheet.js'
import { SavingTo, type SaveChip } from './saving-to.js'
import { uploadQueue, type FileItem } from './upload-queue.js'
import { useOnline, useQueue } from './use-queue.js'
import type { ScopeUpload } from './use-scope-upload.js'
import { useBackLayer } from '../use-activity-history.js'

const PAGE_LAYER = 4
/** The pill over a tile, and the length on a video: ink at 60 %. */
const PILL = 'bg-scrim/60 text-white'

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** *6 photos · 1 video*. */
export function batchWords(files: FileItem[]): string {
  const photos = files.filter((file) => file.uploadKind === 'photo').length
  const videos = files.filter((file) => file.uploadKind === 'video').length
  return [photos > 0 ? count(photos, 'photo', 'photos') : null, videos > 0 ? count(videos, 'video', 'videos') : null]
    .filter(Boolean).join(' · ') || 'Photos'
}

/** *2 photos are saved on this phone*, *1 photo and 1 video are …*. */
function waitingWords(files: FileItem[]): string {
  const photos = files.filter((file) => file.uploadKind === 'photo').length
  const videos = files.filter((file) => file.uploadKind === 'video').length
  const parts = [photos > 0 ? count(photos, 'photo', 'photos') : null, videos > 0 ? count(videos, 'video', 'videos') : null]
    .filter(Boolean)
  const verb = photos + videos === 1 ? 'is' : 'are'
  return `${parts.join(' and ')} ${verb} saved on this phone and go${photos + videos === 1 ? 'es' : ''} up when you’re back online.`
}

const clockOf = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

export function PhotosScreen({
  scope, chips, onTakeMore, onClose,
}: {
  scope: ScopeUpload
  chips: SaveChip[]
  /** Opens the camera again — a press, so the phone allows it. */
  onTakeMore: () => void
  onClose: () => void
}) {
  /* The whole screen: back is its ‹ Stop (Aaron, Oct 26). */
  useBackLayer(onClose)
  const items = useQueue()
  const online = useOnline()
  const files = useMemo(() => items.filter((item): item is FileItem =>
    item.kind === 'file' && item.batch === scope.batch && item.uploadKind !== 'audio'), [items, scope.batch])
  const [selected, setSelected] = useState<string | null>(null)
  const [caption, setCaption] = useState('')
  const [menu, setMenu] = useState(false)
  const urls = useRef(new Map<string, string>())

  /* The newest one is the one being captioned, until another is tapped. */
  const current = files.find((file) => file.clientId === selected) ?? files[files.length - 1] ?? null
  useEffect(() => { setCaption(current?.caption ?? '') }, [current?.clientId])
  useEffect(() => () => { for (const url of urls.current.values()) URL.revokeObjectURL(url) }, [])

  const urlOf = (file: FileItem) => {
    let url = urls.current.get(file.clientId)
    if (!url) { url = URL.createObjectURL(file.blob); urls.current.set(file.clientId, url) }
    return url
  }

  /* A caption is kept as it is typed, and sent with the photo (or after it). */
  useEffect(() => {
    if (!current || caption === (current.caption ?? '')) return undefined
    const timer = window.setTimeout(() => void uploadQueue().setCaption(current.clientId, caption), 400)
    return () => window.clearTimeout(timer)
  }, [caption, current])

  const waiting = files.filter((file) => file.state === 'waiting')
  const kindIndex = current
    ? files.filter((file) => file.uploadKind === current.uploadKind).findIndex((file) => file.clientId === current.clientId) + 1
    : 0

  return createPortal(
    <div className="fixed inset-0 z-[60] flex flex-col bg-surface [line-height:normal]" data-overlay-layer={PAGE_LAYER}
      role="dialog" aria-modal="true" aria-label="Photos and video">
      <div className="flex h-[calc(53px+env(safe-area-inset-top))] shrink-0 items-center gap-2 border-b border-line bg-surface px-3 pt-[env(safe-area-inset-top)]">
        <button type="button" onClick={onClose}
          className="inline-flex items-center gap-0.5 whitespace-nowrap text-[15px] font-medium text-accent">
          <Glyph name="chevronLeft" size={20} />Stop
        </button>
        <span className="flex-1" />
        <b className="text-[15px]">{batchWords(files)}</b>
        <span className="flex-1" />
        <button type="button" aria-label="More" onClick={() => setMenu(true)} className="inline-flex text-text-muted">
          <Glyph name="more" size={20} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-surface px-4 py-3.5">
        <SavingTo chips={chips} />
        {!online && waiting.length > 0 && (
          <div role="status" className={cn('mt-2.5 flex items-start gap-[9px] rounded-[10px] border border-amber-band-line',
            'bg-amber-band-bg px-3 py-2.5 text-[12px] leading-[1.45] text-amber-band-text')}>
            <Glyph name="info" size={15} />
            <div><b>No signal.</b> {waitingWords(waiting)}</div>
          </div>
        )}
        {scope.problem && (
          <p role="alert" className="mt-2.5 text-[12.5px] font-semibold text-danger">{scope.problem}</p>
        )}
        <div className="mt-3 grid grid-cols-[repeat(3,110px)] justify-center gap-1.5">
          {files.map((file) => (
            <button key={file.clientId} type="button" onClick={() => setSelected(file.clientId)}
              aria-label={`${file.uploadKind === 'video' ? 'Video' : 'Photo'}, ${file.state === 'done' ? 'up' : file.state}`}
              aria-pressed={current?.clientId === file.clientId}
              className={cn('relative block overflow-hidden rounded-[10px]',
                current?.clientId === file.clientId && 'shadow-[0_0_0_3px_rgb(var(--color-accent))]')}>
              <span className="relative block h-[110px] w-[110px] overflow-hidden rounded-[7px] bg-line-soft">
                {file.uploadKind === 'video' ? (
                  <>
                    <video src={`${urlOf(file)}#t=0.1`} muted playsInline preload="metadata"
                      className="h-full w-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center text-white">
                      <Glyph name="play" size={18} />
                    </span>
                  </>
                ) : (
                  <img src={urlOf(file)} alt="" className="h-full w-full object-cover" />
                )}
              </span>
              <TileState file={file} />
              {file.uploadKind === 'video' && file.durationSeconds !== undefined && (
                <span className={cn('absolute bottom-[5px] left-[5px] inline-flex h-[18px] items-center rounded-[5px] px-[5px]',
                  'text-[10px] font-bold', PILL)}>{clockOf(file.durationSeconds)}</span>
              )}
            </button>
          ))}
        </div>

        {current && (
          <>
            <div className="mb-[5px] mt-3.5 flex items-center gap-1 text-[11.5px] font-medium text-text-muted">
              Caption · {current.uploadKind === 'video' ? 'video' : 'photo'} {kindIndex}
            </div>
            <label className={cn('flex h-11 items-center gap-2 rounded-[10px] border border-line px-3 text-[14.5px]',
              'focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]')}>
              <input value={caption} onChange={(event) => setCaption(event.target.value)}
                placeholder="Optional" aria-label="Caption"
                className="min-w-0 flex-1 bg-transparent outline-none focus-visible:shadow-none" />
              <Dictation variant="icon" onText={(said) => setCaption((was) => (was ? `${was} ${said}` : said))} />
            </label>
          </>
        )}

        <button type="button" role="switch" aria-checked={scope.alsoShow}
          onClick={() => scope.setAlsoShow(!scope.alsoShow)}
          className="mt-3 flex items-center gap-2.5 text-left text-[13.5px]">
          <span className={cn('inline-flex h-6 w-10 shrink-0 items-center rounded-full p-0.5',
            scope.alsoShow ? 'justify-end bg-accent' : 'justify-start bg-line-strong')}>
            <i className="block h-5 w-5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,.25)]" />
          </span>
          Also show on the property’s photos
        </button>
      </div>

      <div className="flex shrink-0 gap-2 border-t border-line bg-surface px-3.5 pt-2.5 pb-[max(16px,env(safe-area-inset-bottom))]">
        <button type="button" onClick={onTakeMore}
          className="inline-flex h-[46px] w-full items-center justify-center gap-1.5 rounded-lg border border-line bg-surface text-[14px] font-semibold">
          <Glyph name="camera" size={16} />Take more
        </button>
        <button type="button" onClick={onClose}
          className="inline-flex h-[46px] w-full items-center justify-center gap-1.5 rounded-lg border border-accent bg-accent text-[14px] font-semibold text-white">
          <Glyph name="check" size={16} />Done
        </button>
      </div>

      {menu && current && (
        <StopSheet title={`${current.uploadKind === 'video' ? 'Video' : 'Photo'} ${kindIndex}`}
          sub={current.state === 'done' ? 'on the job now' : 'not sent yet'} onClose={() => setMenu(false)}>
          {current.state === 'done' ? (
            <p className="m-0 text-[13px] text-text-muted">
              It is on the job — remove it there, from the job’s Files.
            </p>
          ) : (
            <button type="button"
              onClick={() => { void uploadQueue().withdraw(current.clientId); setSelected(null); setMenu(false) }}
              className="flex h-[52px] w-full items-center gap-3 text-left text-[15px] font-semibold text-danger">
              <Glyph name="close" size={17} />Don’t send this one
            </button>
          )}
        </StopSheet>
      )}
    </div>,
    document.body,
  )
}

/** A tick, a ring, or *waiting* (Board 15 B). */
function TileState({ file }: { file: FileItem }) {
  if (file.state === 'done') {
    return (
      <span className="absolute right-[5px] top-[5px] inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-white">
        <Glyph name="check" size={12} stroke={3} />
      </span>
    )
  }
  if (file.state === 'uploading') {
    const pct = Math.round(Math.max(0.04, file.progress) * 100)
    return (
      <span aria-hidden className="absolute right-[5px] top-[5px] block h-5 w-5 rounded-full"
        style={{ background: `conic-gradient(rgb(255 255 255) 0 ${pct}%, rgb(255 255 255 / .35) ${pct}% 100%)` }} />
    )
  }
  return (
    <span title={file.error} className={cn('absolute right-[5px] top-[5px] inline-flex h-5 items-center gap-[3px] rounded-full px-1.5',
      'text-[10px] font-bold', file.state === 'failed' ? 'bg-danger text-white' : PILL)}>
      <Glyph name={file.state === 'failed' ? 'info' : 'clock'} size={10} stroke={2.4} />
      {file.state === 'failed' ? 'not sent' : 'waiting'}
    </span>
  )
}
